import { FC, useEffect, useMemo, useRef } from 'react';
import { throttle } from 'lodash-es';
import SlideRuler from 'slide-ruler';
import cx from './index.module.scss';

export interface SlideRulerModuleProps {
  value?: number;
  onChange?: (value: SlideRulerModuleProps['value']) => void;
  unit: string;
  label: string;
  maxValue?: number;
  minValue?: number;
  defaultVal?: number;
}

const addDecimalIfInteger = num => {
  if (Number.isInteger(num)) {
    return num + '.0';
  } else {
    return num?.toString();
  }
};

const SlideRulerModule: FC<SlideRulerModuleProps> = props => {
  const { value, onChange, label, unit, defaultVal = 0, minValue = 0, maxValue = 10000 } = props;
  const slideRulerRef = useRef(null);
  const isFirstLoad = useRef<boolean>(true);

  const handleValue = useMemo(
    () =>
      throttle(value => {
        !isFirstLoad.current && onChange(value);
        isFirstLoad.current = false;
      }, 60),
    [onChange]
  );

  useEffect(() => {
    if (slideRulerRef.current) {
      new SlideRuler({
        el: slideRulerRef.current,
        handleValue: handleValue,
        currentValue: value ?? defaultVal,
        precision: 0.1,
        fontSize: 14,
        canvasWidth: 456,
        canvasHeight: 80,
        fontColor: '#000000A6',
        heightDecimal: 34,
        heightDigit: 20,
        colorDecimal: '#0055FF4D',
        colorDigit: '#0055FF1A',
        divide: 8,
        fontMarginTop: 50,
        maxValue,
        minValue,
      });
      return () => {
        slideRulerRef.current = null;
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('slide-ruler-wrapper')}>
      <div className={cx('info')}>
        <div className={cx('label')}>{label}</div>
        <div className={cx('select-value')}>
          <div className={cx('value')}>
            <span>{addDecimalIfInteger(value ?? defaultVal)}</span>
            <span className={cx('unit')}>{unit}</span>
          </div>
        </div>
      </div>
      <div className={cx('slide-ruler')}>
        <div className={cx('current-value')}></div>
        <div ref={slideRulerRef} />
      </div>
    </div>
  );
};

export default SlideRulerModule;
